import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaYoutube, FaMapMarkerAlt, FaPhone, FaEnvelope, FaArrowRight, FaHeart } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import API_URL from '../config/api';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/company`);
      if (response.data.success) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const quickLinks = [
    { name: t('common.home'), path: '/' },
    { name: t('common.about'), path: '/about' },
    { name: t('common.services'), path: '/services' },
    { name: t('common.management'), path: '/management' },
    { name: t('common.contact'), path: '/contact' },
  ];

  const services = [
    { name: t('services.page.bankInstrument.title'), path: '/services' },
    { name: t('services.page.ventureCapital.title'), path: '/services' },
    { name: t('services.page.holding.title'), path: '/services' },
    { name: t('services.page.fintech.title'), path: '/services' },
    { name: t('services.page.crypto.title'), path: '/services' },
    { name: t('services.page.ecommerce.title'), path: '/services' },
    { name: t('services.page.commodity.title'), path: '/services' },
    { name: t('services.page.trading.title'), path: '/services' },
    { name: t('services.page.travel.title'), path: '/services' },
  ];

  const socialLinks = [
    { icon: FaFacebookF, url: companyInfo?.facebook_url, name: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: FaInstagram, url: companyInfo?.instagram_url, name: 'Instagram', color: 'hover:bg-pink-600' },
    { icon: FaLinkedinIn, url: companyInfo?.linkedin_url, name: 'LinkedIn', color: 'hover:bg-blue-700' },
    { icon: FaTwitter, url: companyInfo?.twitter_url, name: 'Twitter', color: 'hover:bg-blue-400' },
    { icon: FaYoutube, url: companyInfo?.youtube_url, name: 'YouTube', color: 'hover:bg-red-600' },
  ];

  return (
    <footer className="bg-gradient-to-br from-navy-900 via-gray-900 to-maroon-900 text-gray-300 relative overflow-hidden pb-16 lg:pb-0">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern opacity-5"></div>
      
      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-6 lg:px-16 xl:px-24 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <h3 className="text-2xl font-display font-bold text-white mb-2">
                  AFTRUSTCA
                </h3>
                <p className="text-gold-400 text-sm font-medium mb-4">
                  Sustainable • Global • Trust
                </p>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-6">
                {companyInfo?.description ? 
                  companyInfo.description.substring(0, 150) + '...' :
                  t('about.description')
                }
              </p>
              
              {/* Social Media */}
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  social.url && (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`bg-white/10 backdrop-blur-sm p-3 rounded-full transition-all duration-300 ${social.color} hover:scale-110 hover:shadow-elegant`}
                      aria-label={social.name}
                    >
                      <social.icon size={16} />
                    </a>
                  )
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white text-lg font-bold mb-6 relative inline-block">
                {t('footer.quickLinks')}
                <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gold-500"></span>
              </h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm hover:text-gold-400 transition-colors duration-300 flex items-center group"
                    >
                      <FaArrowRight className="mr-2 text-gold-500 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-0 -translate-x-2 transition-all" size={12} />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white text-lg font-bold mb-6 relative inline-block">
                {t('footer.ourServices')}
                <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gold-500"></span>
              </h3>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service.path}
                      className="text-sm hover:text-gold-400 transition-colors duration-300 flex items-center group"
                    >
                      <FaArrowRight className="mr-2 text-gold-500 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-0 -translate-x-2 transition-all" size={12} />
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-white text-lg font-bold mb-6 relative inline-block">
                {t('footer.contactInfo')}
                <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gold-500"></span>
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start text-sm group">
                  <FaMapMarkerAlt className="mr-3 mt-1 text-gold-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div className="group-hover:text-white transition-colors">
                    <div className="font-semibold text-white mb-1">PT. ARTHA FINTRUST CONSULTASEAN</div>
                    {i18n.language === 'id' ? (
                      <>
                        <div>18 Office Park</div>
                        <div>Jl. TB Simatupang No. 18</div>
                        <div>Lt. 21 Unit C, Kebagusan</div>
                        <div>Ps. Minggu, Jakarta Selatan</div>
                        <div>DKI Jakarta 12520</div>
                        <div>Indonesia</div>
                      </>
                    ) : (
                      <>
                        <div>18 Office Park</div>
                        <div>#18 TB Simatupang St.</div>
                        <div>21<sup>st</sup> Fl. Unit C, Kebagusan</div>
                        <div>Ps. Minggu, South Jakarta</div>
                        <div>DKI Jakarta 12520 Indonesia</div>
                      </>
                    )}
                  </div>
                </li>
                <li className="flex items-start text-sm group">
                  <FaEnvelope className="mr-3 mt-1 text-gold-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div>
                    <div className="mb-1">
                      <span className="text-gray-400">Email:</span>{' '}
                      <a href="mailto:info@aftrustca.co.id" className="hover:text-gold-400 transition">
                        info@aftrustca.co.id
                      </a>
                    </div>
                    <div>
                      <span className="text-gray-400">Web:</span>{' '}
                      <a href="https://www.aftrustca.co.id" target="_blank" rel="noopener noreferrer" className="hover:text-gold-400 transition">
                        www.aftrustca.co.id
                      </a>
                    </div>
                  </div>
                </li>
                <li className="flex items-start text-sm group">
                  <FaPhone className="mr-3 mt-1 text-gold-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div>
                    <div className="mb-1">
                      <span className="text-gray-400">Office:</span>{' '}
                      <a href="tel:+622131118055" className="hover:text-gold-400 transition">
                        (+62) 21-31118055
                      </a>
                    </div>
                    <div>
                      <span className="text-gray-400">Live Support 24/7:</span>{' '}
                      <a href="https://wa.me/447423369031" target="_blank" rel="noopener noreferrer" className="hover:text-gold-400 transition">
                        +44-7423369031
                      </a>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="container mx-auto px-6 lg:px-16 xl:px-24 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm">
              <p className="text-gray-400 mb-4 md:mb-0">
                &copy; {new Date().getFullYear()} <span className="text-white font-semibold">{companyInfo?.company_name || 'PT. ARTHA FINTRUST CONSULTASEAN'}</span>. {t('footer.copyright')}
              </p>
              <p className="text-gray-400 flex items-center">
                {t('footer.madeWith')} <FaHeart className="text-red-500 mx-2 animate-pulse" /> {t('footer.by')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
